import React, { useState } from "react";
import { RadioCard } from "./RadioCard";
import { SvgIconComponent } from "@mui/icons-material";

export type RadioButton<T extends number | string> = {
  label: React.ReactElement;
  value: T;
  title?: string;
  activeIcon?: SvgIconComponent;
  disabledIcon?: SvgIconComponent;
};

type RadioCardsPropsNotClearable<T> = {
  onChange: (value: T) => void;
  clearable?: undefined | false;
};

type RadioCardsPropsClearable<T> = {
  onChange: (value: T | undefined) => void;
  clearable: true;
};

export type RadioCardsProps<T extends number | string> = {
  options: RadioButton<T>[];
  value: T | undefined;
  label?: React.ReactNode;
  layout?: "horizontal" | "vertical" | "responsive";
  onChange: (value: T) => void;
  clearable?: boolean;
} & (RadioCardsPropsClearable<T> | RadioCardsPropsNotClearable<T>);

export const RadioCards = <T extends number | string>({
  label,
  options,
  value,
  layout = "vertical",
  onChange,
  clearable,
}: RadioCardsProps<T>) => {
  const [selected, setSelected] = useState<T | undefined>(value);

  return (
    <form>
      {label && (
        <label className="subtitle mb-4 text-base font-semibold">{label}</label>
      )}
      <div
        className={`flex ${
          layout === "responsive"
            ? "flex-col space-y-4 lg:flex-row lg:space-x-4 lg:space-y-0"
            : layout === "horizontal"
            ? "flex-row space-x-4"
            : "flex-col space-y-4"
        }`}
      >
        {options.map((option) => {
          return (
            <RadioCard
              key={option.value}
              value={selected === option.value}
              activeIcon={option.activeIcon}
              disabledIcon={option.disabledIcon}
              header={
                option.title ? (
                  <h5 className="subtitle text-center font-semibold text-inherit">
                    {option.title}
                  </h5>
                ) : undefined
              }
              onChange={() => {
                setSelected(option.value);
                onChange(option.value);
              }}
              clear={
                clearable
                  ? () => {
                      setSelected(undefined);
                      onChange(undefined);
                    }
                  : undefined
              }
            >
              {option.label}
            </RadioCard>
          );
        })}
      </div>
    </form>
  );
};
